/**
 * OAuth 1.0 authentication utilities for Penzu API
 * Uses HMAC-SHA256 signature method
 */

interface OAuthCredentials {
  consumerKey: string;
  consumerSecret: string;
  token: string;
  tokenSecret: string;
}

interface OAuthParams {
  oauth_consumer_key: string;
  oauth_nonce: string;
  oauth_signature: string;
  oauth_signature_method: string;
  oauth_timestamp: string;
  oauth_token: string;
  oauth_version: string;
}

/**
 * Generate a random nonce for OAuth requests
 */
function generateNonce(length: number = 32): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join('');
}

/**
 * Get current Unix timestamp in seconds
 */
function getTimestamp(): string {
  return Math.floor(Date.now() / 1000).toString();
}

/**
 * Percent-encode a string according to RFC 3986
 */
function percentEncode(str: string): string {
  return encodeURIComponent(str).replace(/[!'()*]/g, (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase());
}

/**
 * Create the signature base string for OAuth 1.0
 */
function createSignatureBaseString(
  method: string,
  url: string,
  params: Record<string, string>
): string {
  // Sort parameters alphabetically by key
  const sortedKeys = Object.keys(params).sort();
  const paramString = sortedKeys.map((key) => `${percentEncode(key)}=${percentEncode(params[key])}`).join('&');

  // Construct the base string
  return `${method.toUpperCase()}&${percentEncode(url)}&${percentEncode(paramString)}`;
}

/**
 * Create HMAC-SHA256 signature using Web Crypto API
 */
async function createSignature(baseString: string, signingKey: string): Promise<string> {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(signingKey);
  const messageData = encoder.encode(baseString);

  const cryptoKey = await crypto.subtle.importKey('raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);

  // Convert to base64
  const bytes = new Uint8Array(signature);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Generate OAuth 1.0 authorization header
 */
export async function generateOAuthHeader(
  method: string,
  url: string,
  credentials: OAuthCredentials
): Promise<string> {
  const timestamp = getTimestamp();
  const nonce = generateNonce();

  // OAuth parameters (without signature)
  const oauthParams: Record<string, string> = {
    oauth_consumer_key: credentials.consumerKey,
    oauth_nonce: nonce,
    oauth_signature_method: 'HMAC-SHA256',
    oauth_timestamp: timestamp,
    oauth_token: credentials.token,
    oauth_version: '1.0',
  };

  // Create signature base string
  const baseString = createSignatureBaseString(method, url, oauthParams);

  // Create signing key: consumerSecret&tokenSecret
  const signingKey = `${percentEncode(credentials.consumerSecret)}&${percentEncode(credentials.tokenSecret)}`;

  // Generate signature
  const signature = await createSignature(baseString, signingKey);

  // Build authorization header
  const allParams: OAuthParams = {
    ...oauthParams,
    oauth_signature: signature,
  } as OAuthParams;

  const headerParts = Object.keys(allParams)
    .sort()
    .map((key) => `${key}="${percentEncode(allParams[key as keyof OAuthParams])}"`)
    .join(', ');

  return `OAuth ${headerParts}`;
}

/**
 * Get OAuth credentials from the current session in localStorage
 * Returns null if no valid session exists
 */
export function getOAuthCredentialsFromSession(): OAuthCredentials | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const sessionString = window.localStorage.getItem('pz-session');
  if (!sessionString) {
    return null;
  }

  try {
    const session = JSON.parse(sessionString);
    const accessToken = session.access_token;

    if (!accessToken || !accessToken.client_application) {
      return null;
    }

    return {
      consumerKey: accessToken.client_application.key,
      consumerSecret: accessToken.client_application.secret,
      token: accessToken.token,
      tokenSecret: accessToken.secret,
    };
  } catch {
    return null;
  }
}
